<template>
  <div class="result-page">
    <div class="result-card">
      <!-- 成功 -->
      <template v-if="isSuccess">
        <div class="result-icon success">
          <i class="ri-checkbox-circle-fill"></i>
        </div>
        <h1 class="result-title success-text">付款完成</h1>
        <p class="result-message">您的訂閱付款已成功，系統將在幾分鐘內自動更新訂閱狀態。</p>
      </template>

      <!-- 失敗 -->
      <template v-else-if="isFailed">
        <div class="result-icon failed">
          <i class="ri-close-circle-fill"></i>
        </div>
        <h1 class="result-title failed-text">付款失敗</h1>
        <p class="result-message">{{ rtnMsg || '付款未成功，請稍後再試或聯繫客服。' }}</p>
      </template>

      <!-- 無參數（直接訪問此頁） -->
      <template v-else>
        <div class="result-icon neutral">
          <i class="ri-information-line"></i>
        </div>
        <h1 class="result-title">付款結果</h1>
        <p class="result-message">此頁面用於顯示綠界付款結果，請勿直接訪問。</p>
      </template>

      <!-- 訂單資訊 -->
      <div v-if="merchantTradeNo" class="order-info">
        <div class="order-row">
          <span class="order-label">訂單編號：</span>
          <span class="order-value">{{ merchantTradeNo }}</span>
        </div>
        <div v-if="tradeNo" class="order-row">
          <span class="order-label">綠界交易編號：</span>
          <span class="order-value">{{ tradeNo }}</span>
        </div>
        <div v-if="tradeAmt" class="order-row">
          <span class="order-label">交易金額：</span>
          <span class="order-value">NT$ {{ Number(tradeAmt).toLocaleString() }}</span>
        </div>
        <div v-if="paymentDate" class="order-row">
          <span class="order-label">付款時間：</span>
          <span class="order-value">{{ paymentDate }}</span>
        </div>
        <div v-if="paymentType" class="order-row">
          <span class="order-label">付款方式：</span>
          <span class="order-value">{{ paymentType }}</span>
        </div>
      </div>

      <!-- 按鈕 -->
      <div class="result-actions">
        <!-- 加購方案：返回 ChatMate 頁 -->
        <button v-if="isAddon" class="btn btn-primary" @click="goToChatmate">
          <i class="ri-arrow-left-line"></i>
          返回 ChatMate 訂閱
        </button>

        <!-- 主訂閱：返回訂閱頁 -->
        <button v-else-if="merchantTradeNo" class="btn btn-primary" @click="goToSubscription">
          <i class="ri-arrow-left-line"></i>
          返回訂閱頁面
        </button>

        <!-- 無法判斷：返回首頁 -->
        <button v-else class="btn btn-secondary" @click="goHome">
          返回首頁
        </button>
      </div>

      <p v-if="isSuccess" class="auto-redirect-hint">
        {{ countdown }} 秒後自動跳轉...
      </p>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import authService from '@/helpers/authservice/authService.js';

export default {
  name: 'PaymentResult',
  setup() {
    const route = useRoute();
    const router = useRouter();

    // 從 query string 讀取綠界回傳參數
    const rtnCode = ref(route.query.RtnCode || '');
    const rtnMsg = ref(route.query.RtnMsg || '');
    const merchantTradeNo = ref(route.query.MerchantTradeNo || '');
    const tradeNo = ref(route.query.TradeNo || '');
    const tradeAmt = ref(route.query.TradeAmt || '');
    const paymentDate = ref(route.query.PaymentDate || '');
    const paymentType = ref(route.query.PaymentType || '');

    const isSuccess = computed(() => rtnCode.value === '1');
    const isFailed = computed(() => rtnCode.value !== '' && rtnCode.value !== '1');

    // 判斷是加購訂閱（ADDON 前綴）或主訂閱（CRM 前綴）
    const isAddon = computed(() =>
      merchantTradeNo.value.startsWith('ADDON')
    );

    const countdown = ref(5);
    let timer = null;

    const getAccountType = () => authService.getAccountType();

    const goToSubscription = () => {
      const type = getAccountType();
      if (['client'].includes(type)) {
        router.push('/client/subscription');
      } else {
        router.push('/client/login');
      }
    };

    const goToChatmate = () => {
      const type = getAccountType();
      if (['client'].includes(type)) {
        router.push('/client/chatmate/subscription');
      } else {
        router.push('/client/login');
      }
    };

    const goHome = () => {
      const type = getAccountType();
      if (type === 'system') {
        router.push('/system/dashboard');
      } else if (type === 'platform') {
        router.push('/platform/dashboard');
      } else if (['client'].includes(type)) {
        router.push('/client/dashboard');
      } else {
        router.push('/client/login');
      }
    };

    onMounted(() => {
      // 成功時自動倒數跳轉
      if (isSuccess.value) {
        timer = setInterval(() => {
          countdown.value--;
          if (countdown.value <= 0) {
            clearInterval(timer);
            if (isAddon.value) {
              goToChatmate();
            } else {
              goToSubscription();
            }
          }
        }, 1000);
      }
    });

    onUnmounted(() => {
      if (timer) clearInterval(timer);
    });

    return {
      rtnCode,
      rtnMsg,
      merchantTradeNo,
      tradeNo,
      tradeAmt,
      paymentDate,
      paymentType,
      isSuccess,
      isFailed,
      isAddon,
      countdown,
      goToSubscription,
      goToChatmate,
      goHome
    };
  }
};
</script>

<style scoped>
.result-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  padding: 24px;
}

.result-card {
  background: white;
  border-radius: 12px;
  padding: 48px 40px;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.result-icon {
  font-size: 72px;
  margin-bottom: 16px;
  line-height: 1;
}

.result-icon.success { color: #4caf50; }
.result-icon.failed  { color: #f44336; }
.result-icon.neutral { color: #9e9e9e; }

.result-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px;
  color: #333;
}

.success-text { color: #2e7d32; }
.failed-text  { color: #c62828; }

.result-message {
  font-size: 15px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 24px;
}

/* 訂單資訊 */
.order-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px 20px;
  text-align: left;
  margin-bottom: 28px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.order-row {
  display: flex;
  gap: 8px;
  font-size: 14px;
  flex-wrap: wrap;
}

.order-label {
  color: #888;
  min-width: 110px;
  flex-shrink: 0;
}

.order-value {
  color: #333;
  font-weight: 500;
  word-break: break-all;
}

/* 按鈕 */
.result-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.btn {
  padding: 12px 28px;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-primary {
  background: #1976d2;
  color: white;
}

.btn-primary:hover {
  background: #1565c0;
}

.btn-secondary {
  background: #757575;
  color: white;
}

.btn-secondary:hover {
  background: #616161;
}

.auto-redirect-hint {
  font-size: 13px;
  color: #aaa;
  margin: 0;
}
</style>
